import { Box, Stack, TableCell, TableRow, Typography } from "@mui/material";
import RequiredFieldMarker from "@entrilia/shared/components/RequiredFieldMarker";
import { convertToNumber } from "@entrilia/shared/utilities/numberHelper";
import { autoFormatCamelCase, stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import type { ObjectFieldValues } from "../../../../../../api/types/objectTypes";
import type {
  ObjectSelectConfiguration,
  WorkflowActivityEditConfiguration,
} from "../../../../../../api/types/workflowTypes";
import FieldEdit from "../../../../../entityFields/editors/FieldEdit";
import ConfidenceIndicator from "../ConfidenceIndicator";
import InstructionSourceInfoIcon from "../InstructionSourceInfoIcon";
import WorkflowFieldCitationButton from "../WorkflowFieldCitationButton";
import type { FieldState } from "./changeSetEditState";
import ObjectAutocomplete from "./ObjectAutocomplete";
import type { ObjectSelectOptions } from "./objectSelectOptionsHelper";
import { getObjectSelectValue } from "./objectSelectOptionsHelper";

interface Props {
  field: FieldState;
  editConfiguration?: WorkflowActivityEditConfiguration;
  isChangedFromOriginal: boolean;
  objectSelectOptions: ObjectSelectOptions;
  onFieldChange: (fieldName: string, value: unknown) => void;
  onObjectChange: (configuration: ObjectSelectConfiguration, selectedObject: ObjectFieldValues | null) => void;
}

const ChangeSetInlineEditTableRow = ({
  field,
  editConfiguration,
  isChangedFromOriginal,
  objectSelectOptions,
  onFieldChange,
  onObjectChange,
}: Props) => {
  const hasValue = field.newValue !== null && field.newValue !== undefined;
  const confidence = convertToNumber(field.metadata?.Confidence);
  const source = stringifyValue(field.metadata?.Source);
  const sourceDocumentName = stringifyValue(field.metadata?.SourceDocument);
  const isRequired = field.attributes?.includes("Required");
  const objectSelectConfiguration = editConfiguration?.objectSelectConfigurations?.find(
    (configuration) => configuration.fieldName === field.name
  );

  return (
    <TableRow>
      <TableCell width="20%" sx={(t) => ({ minWidth: 150, color: t.palette.text.secondary })}>
        {field.caption}
        {isRequired && <RequiredFieldMarker />}
      </TableCell>
      <TableCell width="65%">
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Box sx={{ flexGrow: 1 }}>
            {objectSelectConfiguration ? (
              <ObjectAutocomplete
                options={objectSelectOptions[objectSelectConfiguration.fieldName] ?? []}
                value={getObjectSelectValue(field.newValue, objectSelectConfiguration, objectSelectOptions)}
                onChange={(value) => onObjectChange(objectSelectConfiguration, value)}
                disabled={field.attributes?.includes("ReadOnly")}
                error={field.hasError}
              />
            ) : (
              <FieldEdit
                value={field.newValue}
                fieldName={field.name}
                fieldType={field.fieldType}
                fieldConfiguration={field.fieldConfiguration}
                fieldAttributes={field.attributes}
                onChange={onFieldChange}
                hasError={field.hasError}
              />
            )}
          </Box>
          <Box sx={{ width: "1rem", flexShrink: 0, display: "flex", alignItems: "center" }}>
            {hasValue && confidence !== undefined && <ConfidenceIndicator confidence={confidence} />}
          </Box>
        </Stack>
      </TableCell>
      <TableCell width="15%" sx={{ minWidth: 100 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <WorkflowFieldCitationButton fieldName={field.name} tooltip={sourceDocumentName} />
          <Typography color="textSecondary">{autoFormatCamelCase(source)}</Typography>
          <InstructionSourceInfoIcon field={field} hidden={isChangedFromOriginal} />
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default ChangeSetInlineEditTableRow;
